'use server'

import { apiRequest, ApiResponse } from '../request'

export interface HrPayrollRun {
    id: number
    /** Filial empregadora — a folha é dela, mesmo vista de outra unidade. */
    companyId: number
    company?: { id: number; name: string } | null
    competence: string
    type: string
    status: string
    /** SYSTEM: calculada aqui. IMPORTED: veio de holerite da contabilidade. */
    origin?: string
    totalEarnings: number
    totalDeductions: number
    totalNet: number
    totalFgts: number
    totalInssCompany: number
    processedAt: string | null
    approvedById: number | null
    approvedBy?: { id: number; name: string } | null
    paidAt: string | null
    notes: string | null
    items?: HrPayrollItem[]
    _count?: { items: number }
    createdAt: string
}

export interface HrPayrollItem {
    id: number
    payrollRunId: number
    employeeId: number
    employee?: {
        id: number
        name: string
        cpf: string | null
        department: string | null
        jobTitle: string | null
        /** Preenchido: salário sai por transferência entre contas, sem tarifa. */
        platformOrganizationId?: number | null
        pixKey?: string | null
    }
    grossSalary: number
    totalEarnings: number
    totalDeductions: number
    netSalary: number
    fgtsBase: number
    fgtsValue: number
    inssBase: number
    inssValue: number
    irrfBase: number
    irrfDeductions: number
    irrfValue: number
    /** Holerite original da contabilidade arquivado no Armazenamento. */
    documentFileId?: number | null
    payslipSentAt?: string | null
    /** Conta a pagar do salário — é ela que carrega a despesa e o pagamento. */
    payableBillingId?: number | null
    payableBilling?: {
        id: number
        status: string
        approvalStatus: string | null
        paidAt: string | null
    } | null
    details?: HrPayrollItemDetail[]
}

export type PayslipMatchStatus = 'MATCHED' | 'SUGGESTED' | 'NOT_FOUND'

export interface PayslipEmployeeCandidate {
    employeeId: number
    name: string
    companyId: number
    companyName: string | null
    status: string
    /** Semelhança do nome, de 0 a 1. */
    score: number
}

/** Holerite lido pela IA e ainda não gravado: uma linha da conferência. */
export interface PayrollImportPreviewItem {
    index: number
    fileName: string
    competence: string
    type: string
    employeeName?: string | null
    employeeCpf?: string | null
    employeeCode?: string | null
    /** Filial dona do CNPJ do holerite; é nela que a folha é aberta. Sem `companyId`, vale a filial do cadastro. */
    employer: { cnpj: string | null; companyId: number | null; companyName: string | null }
    totalEarnings: number
    totalDeductions: number
    netSalary: number
    /** Falso quando a página é dividida com colegas: o funcionário fica sem holerite por e-mail. */
    hasOriginalDocument: boolean
    /** Somas que não fecham. */
    warnings: string[]
    match: {
        status: PayslipMatchStatus
        method?: 'cpf' | 'name'
        employeeId?: number
        candidates: PayslipEmployeeCandidate[]
    }
}

export interface PayrollImportPreview {
    token: string
    expiresAt: string
    items: PayrollImportPreviewItem[]
    failures: Array<{ fileName: string; error: string }>
    employees: Array<{ id: number; name: string; companyId: number; companyName: string | null; status: string }>
}

export interface PayrollImportItemResult {
    fileName: string
    success: boolean
    employeeName?: string
    competence?: string
    payrollRunId?: number
    /** Filial empregadora: o arquivo do grupo alimenta uma folha por filial. */
    companyId?: number
    companyName?: string
    hasOriginalDocument?: boolean
    error?: string
}

export interface PayrollImportResult {
    imported: number
    failed: number
    ignored: number
    payrollRunIds: number[]
    items: PayrollImportItemResult[]
}

export interface HrPayrollItemDetail {
    id: number
    rubricId: number
    rubric?: { id: number; code: string; name: string; type: string }
    description: string | null
    reference: number | null
    value: number
    type: string
}

const BASE = 'hr/payroll'

/** Folhas da organização inteira. `companyId` filtra por filial. */
export async function getPayrollRuns(params?: {
    competence?: string; status?: string; companyId?: number
}): Promise<ApiResponse<HrPayrollRun[]>> {
    return apiRequest<HrPayrollRun[]>(BASE, { params: params || {} })
}

export async function getPayrollRun(id: number): Promise<ApiResponse<HrPayrollRun>> {
    return apiRequest<HrPayrollRun>(`${BASE}/${id}`, {})
}

export async function createPayrollRun(data: { competence: string; type?: string; costCenterId?: number; notes?: string }): Promise<ApiResponse<HrPayrollRun>> {
    return apiRequest<HrPayrollRun>(BASE, { data }, 'post')
}

/**
 * Passo 1 da importação: a IA lê os holerites e devolve cada um com o
 * funcionário encontrado ou sugerido. Nada é gravado.
 */
export async function previewPayslipImport(files: File[]): Promise<ApiResponse<PayrollImportPreview>> {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    return apiRequest<PayrollImportPreview>(
        `${BASE}/import/preview`,
        { data: formData, formData: true },
        'post',
    )
}

/**
 * Passo 2: grava só os holerites confirmados. `employeeId: null` descarta
 * (não é funcionário).
 */
export async function confirmPayslipImport(data: {
    token: string
    decisions: Array<{ index: number; employeeId: number | null }>
}): Promise<ApiResponse<PayrollImportResult>> {
    return apiRequest<PayrollImportResult>(`${BASE}/import/confirm`, { data }, 'post')
}

export async function calculatePayroll(id: number): Promise<ApiResponse<HrPayrollRun>> {
    return apiRequest<HrPayrollRun>(`${BASE}/${id}/calculate`, {}, 'post')
}

export async function approvePayroll(id: number): Promise<ApiResponse<HrPayrollRun>> {
    return apiRequest<HrPayrollRun>(`${BASE}/${id}/approve`, {}, 'post')
}

export async function markPayrollPaid(id: number): Promise<ApiResponse<HrPayrollRun>> {
    return apiRequest<HrPayrollRun>(`${BASE}/${id}/pay`, {}, 'post')
}

export interface PayrollPayablesResult {
    created: number
    items: Array<{ employeeId: number; employeeName: string; billingId: number }>
    skipped: string[]
    errors: string[]
}

/**
 * Envia a folha para pagamento: gera a conta a pagar de cada funcionário
 * (com despesa e lançamento contábil) e as coloca na fila de aprovação
 * financeira. Quem aprova é que transfere.
 */
export async function generatePayrollPayables(
    id: number,
    data?: { dueDate?: string },
): Promise<ApiResponse<PayrollPayablesResult>> {
    return apiRequest<PayrollPayablesResult>(`${BASE}/${id}/generate-payables`, { data: data || {} }, 'post')
}

export async function cancelPayroll(id: number): Promise<ApiResponse<HrPayrollRun>> {
    return apiRequest<HrPayrollRun>(`${BASE}/${id}/cancel`, {}, 'post')
}

export async function deletePayrollRun(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${id}`, {}, 'delete')
}

export async function getPayslip(runId: number, employeeId: number): Promise<ApiResponse<HrPayrollItem>> {
    return apiRequest<HrPayrollItem>(`${BASE}/${runId}/payslip/${employeeId}`, {})
}

export async function downloadPayslipPdf(runId: number, employeeId: number): Promise<void> {
    const { getClient } = await import('../client')
    const client = getClient()
    const res = await client.get(`${BASE}/${runId}/payslip/${employeeId}/pdf`, { responseType: 'blob' })
    const blob = new Blob([res.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 5000)
}

export async function sendPayslips(runId: number): Promise<ApiResponse<{ sent: number; errors: string[] }>> {
    return apiRequest<{ sent: number; errors: string[] }>(`${BASE}/${runId}/send-payslips`, {}, 'post')
}

export async function sendPayslip(runId: number, employeeId: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${runId}/send-payslip/${employeeId}`, {}, 'post')
}
